import { useMemo, useState } from 'react';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useNotifications, useDeleteNotification, useTestNotification } from '../../hooks/useNotifications';
import { useServices } from '../../hooks/useServices';
import { useGroups } from '../../hooks/useGroups';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { Skeleton } from '../common/Skeleton';
import { Modal } from '../common/Modal';
import { useToast } from '../common/Toast';
import { apiError } from '../../utils/apiError';
import { NotificationForm } from './NotificationForm';
import { NotificationDeliveries } from './NotificationDeliveries';
import { describeTargets } from './TargetPicker';
import { providerByType } from './providers';
import { NotificationChannel } from '../../types';
import { useAuth } from '../../context/AuthContext';

/** The one line under the name that says where this channel goes. */
function endpointSummary(c: NotificationChannel): string {
  const cfg = c.config;
  const str = (k: string) => (typeof cfg[k] === 'string' ? (cfg[k] as string) : '');
  switch (c.type) {
    case 'webhook': return `${str('method') || 'POST'} ${str('url')}`;
    case 'slack': case 'discord': case 'teams': return str('webhook_url');
    case 'telegram': return `chat ${str('chat_id')}`;
    case 'ntfy': return `${str('server_url')}/${str('topic')}`;
    case 'gotify': return str('server_url');
    case 'pushover': return str('device') ? `device ${str('device')}` : 'all devices';
    case 'email': return `to ${str('to')} via ${str('host')}`;
    case 'apprise': return str('config_key') ? `${str('server_url')} (config ${str('config_key')})` : str('server_url');
    default: return '';
  }
}

export function NotificationList() {
  useDocumentTitle('Notifications');
  const { data: channels, isLoading } = useNotifications();
  const { data: services } = useServices();
  const { data: groups } = useGroups();
  const deleteChannel = useDeleteNotification();
  const testChannel = useTestNotification();
  const { addToast } = useToast();
  const [editing, setEditing] = useState<NotificationChannel | null>(null);
  const [deleting, setDeleting] = useState<NotificationChannel | null>(null);
  const [viewingDeliveries, setViewingDeliveries] = useState<NotificationChannel | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('notifications.edit');
  const canCreate = hasPermission('notifications.create');
  const canDelete = hasPermission('notifications.delete');

  const lookup = useMemo(() => ({
    services: new Map((services ?? []).map(s => [s.id, s.name])),
    groups: new Map((groups ?? []).map(g => [g.id, g.name])),
  }), [services, groups]);

  function handleDelete() {
    if (!deleting) return;
    deleteChannel.mutate(deleting.id, {
      onSuccess: () => { addToast('success', `Deleted "${deleting.name}"`); setDeleting(null); },
      onError: err => { addToast('error', apiError(err, 'Failed to delete channel')); setDeleting(null); },
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Notifications</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Where confirmed outages, recoveries and reminders are sent.
          </p>
        </div>
        {canCreate && <Button onClick={() => setShowCreate(true)}>Add channel</Button>}
      </div>

      {isLoading ? (
        <div className="space-y-3" aria-busy="true">
          {Array.from({ length: 2 }).map((_, i) => (
            <Card key={i} className="p-4 space-y-2">
              <Skeleton className="h-4 w-1/4" />
              <Skeleton className="h-3 w-2/3" />
            </Card>
          ))}
        </div>
      ) : !channels || channels.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">No notification channels yet.</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Slack, Discord, Teams, Telegram, ntfy, Gotify, Pushover, email, Apprise or any webhook.
          </p>
          {canCreate && <Button className="mt-4" onClick={() => setShowCreate(true)}>Add your first channel</Button>}
        </div>
      ) : (
        <div className="space-y-3">
          {channels.map(c => (
            <Card key={c.id} className="p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-semibold">{c.name}</h2>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                      {providerByType.get(c.type)?.label ?? c.type}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      c.enabled
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {c.enabled ? 'Active' : 'Disabled'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 font-mono break-all">
                    {endpointSummary(c)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {describeTargets(c.targets, lookup)}
                  </p>
                  <div className="flex gap-1.5 mt-2 flex-wrap">
                    {c.events.map(e => (
                      <span key={e} className="px-2 py-0.5 text-xs rounded-full bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300">
                        {e}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0 flex-wrap">
                  {canEdit && (
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={testChannel.isPending && testChannel.variables?.id === c.id}
                      onClick={() => testChannel.mutate({ id: c.id }, {
                        onSuccess: r => addToast(
                          r.success ? 'success' : 'error',
                          r.success
                            ? `Test delivered to "${c.name}"`
                            : `Test failed: ${r.error || `HTTP ${r.status_code}`}`,
                        ),
                        onError: err => addToast('error', apiError(err, 'Failed to send test')),
                      })}
                    >
                      Test
                    </Button>
                  )}
                  <Button variant="secondary" size="sm" onClick={() => setViewingDeliveries(c)}>Deliveries</Button>
                  {canEdit && <Button variant="secondary" size="sm" onClick={() => setEditing(c)}>Edit</Button>}
                  {canDelete && <Button variant="ghost" size="sm" onClick={() => setDeleting(c)}>Delete</Button>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Add notification channel">
        {showCreate && <NotificationForm onClose={() => setShowCreate(false)} />}
      </Modal>

      <Modal open={!!viewingDeliveries} onClose={() => setViewingDeliveries(null)} title={`Deliveries — ${viewingDeliveries?.name ?? ''}`}>
        {viewingDeliveries && <NotificationDeliveries channel={viewingDeliveries} canRedeliver={canEdit} />}
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit notification channel">
        {editing && <NotificationForm key={editing.id} channel={editing} onClose={() => setEditing(null)} />}
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        title="Delete channel"
        message={`"${deleting?.name}" will stop receiving notifications and its delivery history will be removed.`}
        confirmLabel="Delete channel"
        loading={deleteChannel.isPending}
        onConfirm={handleDelete}
        onClose={() => setDeleting(null)}
      />
    </div>
  );
}
